/**
 * @module LocalCart
 * @description A global utility to manage a client-side shopping cart using LocalStorage.
 * Handles item persistence, quantity logic, and Shopify-specific property filtering.
 * * Features:
 * - Persistent storage across sessions.
 * - Automatic "Deposit" (_deposit) exclusion from total item counts.
 * - Real-time Cross-Tab synchronization.
 * - Unified Update/Delete logic.
 * - Unified Cart Clear logic.
 */

const LocalCart = {
    STORAGE_KEY: 'shopify_local_cart',

    //returns {Object} The current cart state from LocalStorage.
    getState() {
        const saved = localStorage.getItem(this.STORAGE_KEY);
        return saved ? JSON.parse(saved) : { items: [], item_count: 0, total_price: 0 };
    },

    /**
     * Internal method to recalculate totals and persist data.
     * Fires 'localCartUpdated' event for UI components.
     * @param {Object} state - The cart state to save.
     */
    save(state) {
        // `getState()` returns a default object with no `attributes` key, so the
        // empty-cart branch below used to throw on a first-ever save.
        if (!state.attributes || typeof state.attributes !== 'object') state.attributes = {};
        if (!Array.isArray(state.items)) state.items = [];

        // Calculate item_count: Ignore items where properties contains '_deposit'
        state.item_count = state.items.reduce((acc, item) => {
            const isDeposit = item.properties && item.properties.hasOwnProperty('_deposit');
            return isDeposit ? acc : acc + 1;
        }, 0);

        // Calculate total_price
        state.original_total_price = state.items.reduce((acc, item) => {
            return acc + (item.price * item.quantity);
        }, 0);
        state.total_price = state.original_total_price
        state.items_subtotal_price = state.original_total_price;

        //when cart is empty
        if(state.item_count <= 0){
            state.cart_level_discount_applications = [];
            state.discount_codes = [];
            //clear bonus amount attribute
            delete state.attributes.bonus_amount;
            delete state.attributes.bonus_amount_max;
        }
        
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
        
        // Notify current tab
        window.dispatchEvent(new CustomEvent('localCartUpdated', { detail: state }));
        return state;
    },

    /**
     * Adds an item or increments quantity if it exists.
     * @param {Object} product - The product object.
     */
    addItem(product) {
        const state = this.getState();
        // Match by ID
        const existingItem = state.items.find(item => item.id === product.id);    
        //check if properties are identical
        if (existingItem && this.arePropertiesEqual(existingItem.properties, product?.properties)){
            existingItem.quantity += (product.quantity || 1);
        } else {
            //add new item entry
            state.items.push({ ...product, quantity: product.quantity || 1 });
        }
        return this.save(state);
    },

    /**
     * Check if properties are equal
     * @param {Object} props1 - The properties object.
     * @param {Object} props2 - The properties object.
     */
    arePropertiesEqual(props1 = {}, props2 = {}) {
        const keys1 = Object.keys(props1 || {});
        const keys2 = Object.keys(props2 || {});

        if (keys1.length !== keys2.length) return false;

        return keys1.every(key => props1[key] === props2[key]);
    },

    /**
     * Sets an exact quantity for a specific ID.
     * If quantity is 0 or less, the item is automatically removed.
     * @param {number} id - The Variant ID to update.
     * @param {number} quantity - The new quantity.
     */
    /**
     * Resolves a line reference to its index in state.items.
     * The same variant id can legitimately occupy several lines (different
     * `_next_delivery_date`, alternative-product parents, etc.), so a unique
     * line `key` is always preferred over the variant `id`.
     * @param {Array} items
     * @param {string|number} ref - a line key or a variant id
     * @returns {number} index, or -1
     */
    resolveLineIndex(items, ref) {
        if (ref == null) return -1;
        const byKey = items.findIndex(item => item.key != null && item.key == ref);
        if (byKey !== -1) return byKey;
        return items.findIndex(item => item.id == ref);
    },

    updateQuantity(ref, quantity) {
        if(!ref) return;
        const state = this.getState();
        const qty = parseInt(quantity);
        const index = this.resolveLineIndex(state.items, ref);

        if (index === -1) return this.save(state);

        if (qty <= 0) {
            // Remove ONLY the matched line. The previous
            // `filter(item => item.id != id)` deleted every line sharing the
            // variant id, silently dropping unrelated cart lines.
            state.items.splice(index, 1);
        } else {
            const item = state.items[index];
            item.quantity = qty;
            // `_price` may be missing; falling through to NaN corrupted every
            // derived line total.
            const unitPrice = parseInt(item.properties?._price, 10);
            if (Number.isFinite(unitPrice)) {
                item.price = unitPrice;
            }
            const safePrice = Number.isFinite(item.price) ? item.price : 0;
            item.final_line_price = safePrice * qty;
            item.original_line_price = item.final_line_price;
            item.line_price = item.final_line_price;
            item.discounted_price = safePrice;
        }

        return this.save(state);
    },

    /**
     * Sets an exact properties for a specific ID.
     * @param {object} properties - The updated properties object.
     */
    updateProps(ref, properties) {
        const state = this.getState();
        // Prefer the unique line key; fall back to variant id for legacy callers.
        const index = this.resolveLineIndex(state.items, ref);
        if (index === -1) return;
        // Update properties only if provided
        if(properties) state.items[index].properties = properties;
        return this.save(state);
    },

    /**
     * Sets attribute.
     * @param {object} attribute - The updated attribute object.
     */
    updateAttr(attr) {
        const state = this.getState();
        if(attr) state.attributes = {...state.attributes, ...attr};
        return this.save(state);
    },

    /**
     * Sets attribute.
     * @param {object} attribute - The updated attribute object.
     */
    updateCheckoutState(checkout_started = false) {
        const state = this.getState();
        state.is_checkout_started = checkout_started;
        return this.save(state);
    },

    /**
     * Clear local cart
     */
    clearLocalCart(clearCheckoutId = false){
        const state = this.getState();
        // Guard: the default state carries no `attributes` object.
        if (!state.attributes || typeof state.attributes !== 'object') state.attributes = {};
        state.cart_level_discount_applications = [];
        state.discount_codes = [];
        state.item_count = 0;
        state.items = [];
        state.items_subtotal_price = 0;
        state.original_total_price = 0;
        state.total_price = 0;

        //clear bonus amount attribute
        delete state.attributes.bonus_amount;
        delete state.attributes.bonus_amount_max;
        delete state.attributes.locked;
        //clear checkoutId attribute
        if(clearCheckoutId){
            delete state.attributes.checkout_id;
            delete state.attributes.checkout_id_timestamp;
            delete state.attributes.is_offline_order;
        }
        
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
    },

    /**
     * Update cart count element
     * @param {Object} cart - The cart object.
     */
    updateCartCount(cart){
        if(!cart || Object.keys(cart).length === 0){
            return;
        }
        const countBadge = document.querySelectorAll('cart-count');
        if (countBadge.length) {
            countBadge.forEach(element => element.textContent = `${cart?.item_count ?? 0} Artikel`);
        }
    },

    /**
     * Get particular cart item
     * @param {String} line item id
     */
    getCartItem(id){
        if(!id) return;
        const state = this.getState();
        // Match by ID and return mathing item object
        return state.items.find(item => item.id == id);
    },

    /**
     * Cart count element loader
     * @param {boolean}.
     */
    cartCountEleLoader(value){
        //The toggle() method accepts a second optional boolean argument (force). If the argument is true, the class is added; 
        // if it is false, the class is removed. 
        const cartCountEle = document.getElementById(`cart-icon-bubble`);
        if(cartCountEle){
            cartCountEle.classList.toggle(`loading`,value);
            cartCountEle.querySelector(`.cart-icon-bubble-loading`)?.classList.toggle(`hidden`,!value);
        }
    }
};

/**
 * Cross-Tab Sync Logic
 */
window.addEventListener('storage', (event) => {
    if (event.key === LocalCart.STORAGE_KEY) {
        let newState = null;
        try {
            newState = event.newValue ? JSON.parse(event.newValue) : null;
        } catch (e) {
            console.warn('⚠️ Ignoring malformed cross-tab cart payload:', e.message);
        }
        if (newState) {
            window.dispatchEvent(new CustomEvent('localCartUpdated', { detail: newState }));
        }
    }

    //refresh the assortment tab if tab is open
    if (event.key === 'tab_refresh_trigger' && document.body.classList.contains(`template--my-assortment`)) {
        window.location.reload();
    }
});

window.addEventListener('localCartUpdated', (e) => {
    const cart = e.detail;
    LocalCart.updateCartCount(cart);
});

if(!window.customer){
    LocalCart.updateCartCount(LocalCart.getState());
}



/**
 * Function usage
 * Adding item
 * LocalCart.addItem({
        id: 40559350186113,
        price: 189,
        quantity: 1,
        title: "Sammys Super Sandwich Toast",
        sku: "8111758",
        image: "https://...",
        properties: { "_identifier": "8111758" }
    });

 * Update quantity
 * LocalCart.updateQuantity(variantId, newQty);
 * 
 * Update properties
 * LocalCart.updateProps(variantId, properties)
 */